'use client';

import { useEffect, useState } from 'react';

/**
 * The current moment, read in the browser and refreshed on a timer.
 *
 * Null until mounted: these pages are static and cached, so the clock has to be
 * read client-side, and the first render must match the server's, which has no
 * clock at all. Callers render a true, time-free fallback for the null case.
 *
 * One shared source means the nav status and the hours table can never
 * disagree about what day it is, and a page left open across midnight moves
 * both of them on together.
 *
 * The timer stops while the tab is hidden and the clock is re-read the moment
 * it is shown again, so a tab left in the background does no work and is never
 * stale when the visitor comes back to it.
 */
export function useLondonNow(intervalMs = 60_000): Date | null {
  const [now, setNow] = useState<Date | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const tick = () => setNow(new Date());
    const start = () => {
      if (timer === null) timer = setInterval(tick, intervalMs);
    };
    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stop();
      } else {
        tick();
        start();
      }
    };

    // First read on the next tick, so the effect does not set state
    // synchronously and cascade a second render before this one commits.
    const initial = setTimeout(tick, 0);
    if (document.visibilityState !== 'hidden') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(initial);
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs]);

  return now;
}
